import { isNewImage } from './imageHelpers';
import { calculateSortOrderRank, rebalanceSortOrderRanks } from './sortOrderHelpers';

/**
 * @param {Array} prevImages - Previous image array
 * @param {Array} newImages - New image array
 * @returns {boolean} True if reordering detected
 */
export const hasImageReordering = (prevImages, newImages) => {
  return (
    prevImages.length === newImages.length &&
    prevImages.every(oldImg => newImages.some(newImg => newImg.id === oldImg.id)) &&
    newImages.some((img, idx) => prevImages[idx]?.id !== img.id)
  );
};

/**
 * @param {Array} prevImages - Previous image array
 * @param {Array} newImages - New image array
 * @returns {Array} Updated images with new sortOrderRanks
 */
export const handleImageReordering = (prevImages, newImages) => {
  let updatedImages = [...newImages];
  let needsRebalancing = false;

  for (const [i, currentImage] of updatedImages.entries()) {
    const previousIndex = prevImages.findIndex(img => img.id === currentImage.id);

    if (previousIndex !== i && !isNewImage(currentImage.id)) {
      const newSortOrderRank = calculateSortOrderRank(updatedImages, i);

      if (newSortOrderRank === null) {
        needsRebalancing = true;
      } else {
        updatedImages[i] = {
          ...currentImage,
          metadata: {
            ...currentImage.metadata,
            sortOrderRank: newSortOrderRank
          }
        };
      }

      break;
    }
  }

  if (needsRebalancing) {
    updatedImages = rebalanceSortOrderRanks(updatedImages);
  }

  return updatedImages;
};

/**
 * @param {Array} prevImages - Previous image array
 * @param {Array} newImages - New image array
 * @param {Function} trackImageDeleted - Callback to track deleted images
 */
export const detectDeletedImages = (prevImages, newImages, trackImageDeleted) => {
  for (const oldImage of prevImages) {
    const newImage = newImages.find(img => img.id === oldImage.id);

    if (!newImage) {
      trackImageDeleted(oldImage.id);
    }
  }
};

/**
 * @param {Array} prevImages - Previous image array
 * @param {Array} newImages - New image array
 * @param {Function} trackImageModified - Callback to track modified images
 * @param {Function} trackImageAdded - Callback to track added images
 */
export const detectModifiedImages = (prevImages, newImages, trackImageModified, trackImageAdded) => {
  for (const oldImage of prevImages) {
    const newImage = newImages.find(img => img.id === oldImage.id);

    if (newImage) {
      const urlChanged = oldImage.url !== newImage.url;
      const metadataChanged =
        oldImage.metadata.featured !== newImage.metadata.featured ||
        oldImage.metadata.cover !== newImage.metadata.cover ||
        oldImage.metadata.style !== newImage.metadata.style ||
        oldImage.metadata.sortOrderRank !== newImage.metadata.sortOrderRank;

      if (urlChanged || metadataChanged) {
        if (isNewImage(oldImage.id)) {
          // For new images, update the 'added' array with the latest metadata
          trackImageAdded(newImage);
        } else {
          // For existing images, track as modified
          trackImageModified(oldImage.id, newImage);
        }
      }
    }
  }
};

/**
 * @param {Array} prevImages - Previous image array
 * @param {Array} newImages - New image array
 * @param {Function} trackImageAdded - Callback to track added images
 */
export const detectAddedImages = (prevImages, newImages, trackImageAdded) => {
  for (const newImage of newImages) {
    if (isNewImage(newImage.id)) {
      const oldImage = prevImages.find(img => img.id === newImage.id);
      const isCompletelyNew = !oldImage;
      const hasUrlChanged = oldImage && oldImage.url !== newImage.url;

      if (isCompletelyNew || hasUrlChanged) {
        trackImageAdded(newImage);
      }
    }
  }
};

/**
 * @param {Array} prevImages - Previous image array
 * @param {Array} newImages - New image array
 * @param {Object} trackingCallbacks - Object with tracking callback functions
 * @returns {Array} Final processed images
 */
export const processImageChanges = (prevImages, newImages, trackingCallbacks) => {
  const { trackImageDeleted, trackImageModified, trackImageAdded } = trackingCallbacks;
  let processedImages = newImages;

  // Handle reordering first
  if (hasImageReordering(prevImages, newImages)) {
    processedImages = handleImageReordering(prevImages, newImages);
  }

  // Detect all types of changes
  detectDeletedImages(prevImages, processedImages, trackImageDeleted);
  detectModifiedImages(prevImages, processedImages, trackImageModified, trackImageAdded);
  detectAddedImages(prevImages, processedImages, trackImageAdded);

  return processedImages;
};
